// Enhanced solution for matching clause text across formatted (multi-node) paragraphs
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';

// Create a unique plugin key
export const riskHighlightPluginKey = new PluginKey('riskHighlight');

// Helper function to escape special RegExp characters
function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Helper to normalize text for comparison (collapse whitespace)
export function normalizeText(text) {
    return text.replace(/\s+/g, ' ').trim();
}

// Legacy helper — still used by DetailsCard.js for text replacement
export function splitIntoParagraphs(html) {
    if (!html) return [];
    let text = html.replace(/<br\s*\/?>/gi, '|PARAGRAPH_BREAK|');
    text = text.replace(/<[^>]*>/g, '');
    const paragraphs = text.split('|PARAGRAPH_BREAK|');
    return paragraphs.map(p => normalizeText(p)).filter(p => p.length > 0);
}

// Extract plain text from HTML clause text (strip tags, normalize)
export function extractClauseText(html) {
    if (!html) return '';
    // Remove <br> tags (they separate lines within a clause, not real paragraph breaks)
    let text = html.replace(/<br\s*\/?>/gi, ' ');
    // Remove all other HTML tags
    text = text.replace(/<[^>]*>/g, '');
    return normalizeText(text);
}

/**
 * Collect concatenated text content of each block-level node (paragraph, heading, etc.)
 * along with a position map so we can convert match offsets back to document positions.
 *
 * Returns: [{ text: string, maps: [{ offset, pos, length }] }, ...]
 *   - text: concatenated plain text of the block
 *   - maps: array of { offset (in concatenated text), pos (doc position), length (chars) }
 */
function collectBlocks(doc) {
    const blocks = [];

    doc.descendants((node, pos) => {
        // Only process block-level content nodes (paragraph, heading, etc.)
        if (node.isBlock && node.isTextblock) {
            const maps = [];
            let offset = 0;
            let text = '';

            node.forEach((child, childOffset) => {
                if (child.isText) {
                    const childPos = pos + 1 + childOffset; // +1 for the opening tag of the block node
                    maps.push({ offset, pos: childPos, length: child.text.length });
                    text += child.text;
                    offset += child.text.length;
                }
            });

            if (text.length > 0) {
                blocks.push({ text, maps, from: pos, to: pos + node.nodeSize });
            }
            return false; // don't descend further into this block
        }
        return true;
    });

    return blocks;
}

/**
 * Given a match range [matchStart, matchEnd) in a concatenated block text,
 * convert it to an array of [docFrom, docTo] ranges using the position map.
 */
function mapMatchToDocPositions(maps, matchStart, matchEnd) {
    const ranges = [];

    for (const { offset, pos, length } of maps) {
        const nodeEnd = offset + length;

        // Skip nodes entirely before the match
        if (nodeEnd <= matchStart) continue;
        // Stop if we've passed the match entirely
        if (offset >= matchEnd) break;

        // Compute overlap within this text node
        const overlapStart = Math.max(matchStart, offset);
        const overlapEnd = Math.min(matchEnd, nodeEnd);

        const docFrom = pos + (overlapStart - offset);
        const docTo = pos + (overlapEnd - offset);

        ranges.push([docFrom, docTo]);
    }

    return ranges;
}

/**
 * Find clause text in the document by searching across concatenated block text.
 * Calls callback(from, to) for each decoration range.
 */
function findTextInDocument(doc, searchText, callback) {
    const normalizedSearch = normalizeText(searchText);
    if (normalizedSearch.length < 5) return;

    const blocks = collectBlocks(doc);
    const escapedSearch = escapeRegExp(normalizedSearch);
    const searchRegex = new RegExp(escapedSearch, 'gi');

    // Strategy 1: Try to find the full clause text within a single block
    for (const block of blocks) {
        const normalizedBlock = normalizeText(block.text);
        let match;
        searchRegex.lastIndex = 0;

        while ((match = searchRegex.exec(normalizedBlock)) !== null) {
            // Build a char-level position map for the normalized text back to the original
            const normToOrigMap = buildNormalizationMap(block.text);
            const origStart = normToOrigMap[match.index];
            const origEnd = normToOrigMap[match.index + match[0].length] ?? (block.text.length);

            const ranges = mapMatchToDocPositions(block.maps, origStart, origEnd);
            for (const [from, to] of ranges) {
                callback(from, to);
            }
        }
    }
}

/**
 * Build a mapping from normalized-text indices to original-text indices.
 * normalizeText collapses runs of whitespace into single spaces and trims.
 * This map lets us convert a match position in normalized text back to the original.
 *
 * Returns an array where map[normalizedIndex] = originalIndex
 */
function buildNormalizationMap(original) {
    const map = [];
    let i = 0; // index into original
    const len = original.length;

    // Skip leading whitespace (trim)
    while (i < len && /\s/.test(original[i])) i++;

    while (i < len) {
        if (/\s/.test(original[i])) {
            // Whitespace run → single space in normalized output
            map.push(i); // the space character maps to start of whitespace run
            while (i < len && /\s/.test(original[i])) i++;
        } else {
            map.push(i);
            i++;
        }
    }

    // Sentinel: one past the end
    map.push(i);

    return map;
}

/**
 * Find clause text spanning multiple consecutive blocks.
 * Used as fallback when full text isn't found in a single block.
 */
function findTextAcrossBlocks(doc, searchText, callback) {
    const normalizedSearch = normalizeText(searchText);
    if (normalizedSearch.length < 5) return false;

    const blocks = collectBlocks(doc);
    let found = false;

    // Try matching across consecutive blocks by joining them
    for (let startIdx = 0; startIdx < blocks.length; startIdx++) {
        let combined = '';
        let combinedMaps = [];
        let globalOffset = 0;

        for (let endIdx = startIdx; endIdx < blocks.length && endIdx < startIdx + 20; endIdx++) {
            const block = blocks[endIdx];

            if (endIdx > startIdx) {
                // Add a space separator between blocks
                globalOffset = combined.length + 1;
                combined += ' ';
            } else {
                globalOffset = 0;
            }

            // Remap this block's maps to global offsets
            for (const m of block.maps) {
                combinedMaps.push({
                    offset: globalOffset + m.offset,
                    pos: m.pos,
                    length: m.length
                });
            }
            combined += block.text;

            // Check if our search text is in the combined text so far
            const normalizedCombined = normalizeText(combined);
            if (normalizedCombined.length < normalizedSearch.length) continue;

            const escapedSearch = escapeRegExp(normalizedSearch);
            const regex = new RegExp(escapedSearch, 'i');
            const match = normalizedCombined.match(regex);

            if (match) {
                const normMap = buildNormalizationMap(combined);
                const origStart = normMap[match.index];
                const origEnd = normMap[match.index + match[0].length] ?? combined.length;

                const ranges = mapMatchToDocPositions(combinedMaps, origStart, origEnd);
                for (const [from, to] of ranges) {
                    callback(from, to);
                }
                found = true;
                break; // Found for this startIdx, move on
            }
        }
        if (found) break;
    }

    return found;
}

export const RiskHighlight = Extension.create({
    name: 'riskHighlight',

    addOptions() {
        return {
            highlightClass: 'risk-highlight',
            highlightMarker: 'data-risk-highlight'
        };
    },

    addStorage() {
        return {
            errorText: '',
            isActive: false,
            highlightColor: '',
        };
    },

    addProseMirrorPlugins() {
        const { highlightClass, highlightMarker } = this.options;

        return [
            new Plugin({
                key: riskHighlightPluginKey,
                state: {
                    init() {
                        return DecorationSet.empty;
                    },
                    apply(tr, set) {
                        set = set.map(tr.mapping, tr.doc);

                        const highlightData = tr.getMeta(riskHighlightPluginKey);

                        if (highlightData === 'clear') {
                            return DecorationSet.empty;
                        } else if (highlightData && typeof highlightData === 'object' && (highlightData.searchText || highlightData.searchTexts)) {
                            const decorations = [];
                            const { color = '#d32f2f' } = highlightData;
                            const { doc } = tr;

                            // Support both single text and array of texts
                            const textsToSearch = highlightData.searchTexts
                                ? highlightData.searchTexts
                                : [highlightData.searchText];

                            const addDeco = (from, to) => {
                                decorations.push(
                                    Decoration.inline(from, to, {
                                        class: highlightClass,
                                        [highlightMarker]: 'true',
                                        style: `background-color: ${color}20; border-bottom: 2px solid ${color}`
                                    })
                                );
                            };

                            for (const searchText of textsToSearch) {
                                if (!searchText) continue;
                                const before = decorations.length;
                                findTextInDocument(doc, searchText, addDeco);
                                if (decorations.length === before) {
                                    findTextAcrossBlocks(doc, searchText, addDeco);
                                }
                            }

                            console.log(`Created ${decorations.length} highlight decorations with color ${color}`);

                            if (decorations.length > 0) {
                                return DecorationSet.create(tr.doc, decorations);
                            }
                            return DecorationSet.empty;
                        }

                        return set;
                    },
                },
                props: {
                    decorations(state) {
                        return this.getState(state);
                    },
                },
            }),
        ];
    },

    addCommands() {
        return {
            highlightRiskText: (errorText, color = '#d32f2f') => ({ tr, dispatch }) => {
                try {
                    // Store state
                    this.storage.errorText = errorText;
                    this.storage.isActive = !!errorText;
                    this.storage.highlightColor = color;

                    // Extract clean text from the HTML clause text
                    const searchText = extractClauseText(errorText);

                    console.log('Highlighting clause text:', searchText.substring(0, 80) + '...');

                    if (dispatch) {
                        tr.setMeta(riskHighlightPluginKey, {
                            searchText,
                            color
                        });
                        dispatch(tr);
                    }

                    return true;
                } catch (error) {
                    console.error('Error in highlightRiskText:', error);
                    return false;
                }
            },
            highlightMultipleTexts: (searchTexts, color = '#c9a96e') => ({ tr, dispatch }) => {
                try {
                    this.storage.isActive = true;
                    this.storage.highlightColor = color;

                    if (dispatch) {
                        tr.setMeta(riskHighlightPluginKey, { searchTexts, color });
                        dispatch(tr);
                    }
                    return true;
                } catch (error) {
                    console.error('Error in highlightMultipleTexts:', error);
                    return false;
                }
            },
            clearRiskHighlight: () => ({ tr, dispatch }) => {
                try {
                    this.storage.errorText = '';
                    this.storage.isActive = false;

                    if (dispatch) {
                        tr.setMeta(riskHighlightPluginKey, 'clear');
                        dispatch(tr);
                    }

                    return true;
                } catch (error) {
                    console.error('Error in clearRiskHighlight:', error);
                    return false;
                }
            },
        };
    },
});
